import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Terminal, Loader2, CheckCircle2, Copy } from "lucide-react";

const PRESTIGES = [
  ...Array.from({ length: 11 }, (_, i) => String(i)),
  "Master Prestige",
];

const initialState = {
  prestige: "",
  level: "",
  psn_id: "",
  discord_username: "",
  notes: "",
};

export default function OrderTerminal({
  apiUrl,
  packages = [],
  selectedPackageId,
  onPackageChange,
  onSuccess,
  confirmed,
}) {
  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === selectedPackageId) || packages[0],
    [packages, selectedPackageId]
  );
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setErrors({});
  }, [selectedPackageId]);

  const setField = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!selectedPackage) e.package = "Select a package";
    if (selectedPackage?.requires_prestige && !form.prestige)
      e.prestige = "Select a prestige";
    if (selectedPackage?.requires_level) {
      const lvl = Number(form.level);
      if (!form.level || Number.isNaN(lvl) || lvl < 1 || lvl > 55)
        e.level = "Level must be 1–55";
    }
    if (!form.psn_id || form.psn_id.trim().length < 3) e.psn_id = "Enter PSN ID (3+ chars)";
    if (!form.discord_username || form.discord_username.trim().length < 2)
      e.discord_username = "Enter Discord username";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (evt) => {
    evt.preventDefault();
    if (!validate()) {
      toast.error("Check the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        package: selectedPackage.id,
        psn_id: form.psn_id.trim(),
        discord_username: form.discord_username.trim(),
        notes: form.notes.trim(),
      };
      if (selectedPackage.requires_prestige) body.prestige = form.prestige;
      if (selectedPackage.requires_level) body.level = Number(form.level);

      const res = await fetch(`${apiUrl}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || "Order failed");
      }
      const data = await res.json();
      onSuccess?.(data);
      toast.success("Order initiated — send payment to complete", {
        description: `Order #${data.order_id.slice(0, 8).toUpperCase()}`,
      });
      const el = document.getElementById("order");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      toast.error("Order failed", { description: String(err.message || err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <ConfirmationPanel
        order={confirmed}
        onReset={() => {
          onSuccess?.(null);
          setForm(initialState);
        }}
      />
    );
  }

  return (
    <div className="relative border border-zinc-800 bg-[#0a0a0a] bo2-corners" data-testid="order-terminal">
      <header className="flex items-center justify-between border-b border-zinc-800 bg-black px-5 py-3">
        <div className="flex items-center gap-3">
          <Terminal className="h-4 w-4 text-[#ff6b00]" />
          <span className="font-mono-hud text-xs uppercase tracking-widest text-zinc-400">
            command_terminal <span className="text-[#ff6b00]">// order.exe</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 bg-[#ff6b00] bo2-pulse" />
          <span className="font-mono-hud text-[10px] uppercase tracking-widest text-zinc-500">
            LIVE
          </span>
        </div>
      </header>

      <form onSubmit={onSubmit} className="grid gap-6 p-6 sm:p-8" noValidate>
        <FieldWrap label="Select Package" testId="field-package" error={errors.package}>
          <Select value={selectedPackage?.id || ""} onValueChange={(v) => onPackageChange?.(v)}>
            <SelectTrigger
              data-testid="package-select"
              className="rounded-none border-zinc-800 bg-black font-mono-hud text-zinc-100 focus:ring-1 focus:ring-[#ff6b00] focus:ring-offset-0 focus-visible:ring-1 focus-visible:ring-[#ff6b00]"
            >
              <SelectValue placeholder="— select package —" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-zinc-800 bg-black">
              {packages.map((p) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  className="rounded-none font-mono-hud text-zinc-200 focus:bg-[#ff6b00]/20 focus:text-[#ff6b00]"
                  data-testid={`package-option-${p.id}`}
                >
                  {p.name} — ${p.price}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldWrap>

        {selectedPackage?.requires_prestige ? (
          <FieldWrap
            label="Target Prestige"
            testId="field-prestige"
            error={errors.prestige}
            hint="00 – 10 or Master"
          >
            <Select value={form.prestige} onValueChange={setField("prestige")}>
              <SelectTrigger
                data-testid="prestige-select"
                className="rounded-none border-zinc-800 bg-black font-mono-hud text-zinc-100 focus:ring-1 focus:ring-[#ff6b00] focus:ring-offset-0 focus-visible:ring-1 focus-visible:ring-[#ff6b00]"
              >
                <SelectValue placeholder="— select prestige —" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-zinc-800 bg-black">
                {PRESTIGES.map((p) => (
                  <SelectItem
                    key={p}
                    value={p}
                    className="rounded-none font-mono-hud text-zinc-200 focus:bg-[#ff6b00]/20 focus:text-[#ff6b00]"
                    data-testid={`prestige-option-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {p === "Master Prestige" ? "★ Master Prestige" : `Prestige ${p}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldWrap>
        ) : null}

        {selectedPackage?.requires_level ? (
          <FieldWrap label="Desired Level (1–55)" testId="field-level" error={errors.level}>
            <Input
              type="number"
              min={1}
              max={55}
              value={form.level}
              onChange={(e) => setField("level")(e.target.value)}
              placeholder="e.g. 55"
              data-testid="level-input"
              className="rounded-none border-zinc-800 bg-black font-mono-hud text-zinc-100 placeholder:text-zinc-600 focus-visible:border-[#ff6b00] focus-visible:ring-1 focus-visible:ring-[#ff6b00] focus-visible:ring-offset-0"
            />
          </FieldWrap>
        ) : null}

        <div className="grid gap-6 sm:grid-cols-2">
          <FieldWrap label="PSN ID" testId="field-psn" error={errors.psn_id}>
            <Input
              value={form.psn_id}
              onChange={(e) => setField("psn_id")(e.target.value)}
              placeholder="YourPSNHandle"
              data-testid="psn-input"
              className="rounded-none border-zinc-800 bg-black font-mono-hud text-zinc-100 placeholder:text-zinc-600 focus-visible:border-[#ff6b00] focus-visible:ring-1 focus-visible:ring-[#ff6b00] focus-visible:ring-offset-0"
            />
          </FieldWrap>
          <FieldWrap label="Discord Username" testId="field-discord" error={errors.discord_username}>
            <Input
              value={form.discord_username}
              onChange={(e) => setField("discord_username")(e.target.value)}
              placeholder="yourname or yourname#0000"
              data-testid="discord-input"
              className="rounded-none border-zinc-800 bg-black font-mono-hud text-zinc-100 placeholder:text-zinc-600 focus-visible:border-[#ff6b00] focus-visible:ring-1 focus-visible:ring-[#ff6b00] focus-visible:ring-offset-0"
            />
          </FieldWrap>
        </div>

        <FieldWrap label="Extra Notes (optional)" testId="field-notes">
          <Textarea
            value={form.notes}
            onChange={(e) => setField("notes")(e.target.value)}
            placeholder="Any custom emblem, requested loadout, etc."
            data-testid="notes-input"
            rows={3}
            className="rounded-none border-zinc-800 bg-black font-mono-hud text-zinc-100 placeholder:text-zinc-600 focus-visible:border-[#ff6b00] focus-visible:ring-1 focus-visible:ring-[#ff6b00] focus-visible:ring-offset-0"
          />
        </FieldWrap>

        <div className="mt-2 flex flex-col gap-4 border-t border-zinc-900 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-mono-hud text-xs uppercase tracking-widest text-zinc-500">
              Total Due
            </div>
            <div className="font-display text-3xl text-[#ff6b00]" data-testid="order-total">
              ${selectedPackage?.price ?? 0}.00{" "}
              <span className="text-sm text-zinc-500">USD</span>
            </div>
            {selectedPackage ? (
              <div className="mt-1 font-mono-hud text-[11px] uppercase tracking-widest text-zinc-500">
                {selectedPackage.name}
              </div>
            ) : null}
          </div>
          <button
            type="submit"
            disabled={submitting || !selectedPackage}
            data-testid="order-submit-button"
            className="group inline-flex items-center gap-3 border-2 border-[#ff6b00] bg-[#ff6b00] px-8 py-4 font-display text-lg uppercase tracking-widest text-black transition-transform duration-150 hover:-translate-y-1 hover:bg-[#ff8125] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Transmitting…
              </>
            ) : (
              <>Initiate Order</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldWrap({ label, hint, error, testId, children }) {
  return (
    <div className="grid gap-2" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <Label className="font-mono-hud text-xs uppercase tracking-[0.3em] text-zinc-400">
          <span className="text-[#ff6b00]">▶</span> {label}
        </Label>
        {hint ? (
          <span className="font-mono-hud text-[10px] uppercase tracking-widest text-zinc-600">
            {hint}
          </span>
        ) : null}
      </div>
      {children}
      {error ? (
        <div
          className="font-mono-hud text-xs uppercase tracking-widest text-red-500"
          data-testid={`${testId}-error`}
        >
          ⚠ {error}
        </div>
      ) : null}
    </div>
  );
}

function ConfirmationPanel({ order, onReset }) {
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied", { description: text });
  };
  return (
    <div className="relative border border-[#ff6b00]/50 bg-[#0a0a0a] bo2-corners" data-testid="order-confirmation">
      <header className="flex items-center justify-between border-b border-zinc-800 bg-black px-5 py-3">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-4 w-4 text-[#ff6b00]" />
          <span className="font-mono-hud text-xs uppercase tracking-widest text-zinc-400">
            order_received <span className="text-[#ff6b00]">// pending_payment</span>
          </span>
        </div>
        <span className="font-mono-hud text-[10px] uppercase tracking-widest text-[#ff6b00]">
          ✔ ROUTED TO DISCORD
        </span>
      </header>

      <div className="p-6 sm:p-8">
        <div className="font-mono-hud text-xs uppercase tracking-[0.4em] text-[#ff6b00]">
          // Order Confirmed
        </div>
        <h3 className="mt-3 font-display text-4xl uppercase tracking-tight text-zinc-100">
          Send Payment to <span className="text-[#ff6b00]">Complete</span>
        </h3>
        <p className="mt-3 font-hud text-zinc-400">
          Your order for <span className="text-[#ff6b00]">{order.package_name}</span> is
          logged. Send <span className="text-[#ff6b00]">${order.price_usd}</span> via
          CashApp to <span className="text-[#ff6b00]">{order.cashapp_handle}</span>.
          Biipod will DM you on Discord once payment is verified. Delivery takes ~5
          minutes once your order is picked up — there may be a short queue during
          high-volume windows.
        </p>

        <dl className="mt-8 grid gap-px bg-zinc-900 sm:grid-cols-2">
          <Cell label="Order ID">
            <button
              onClick={() => copy(order.order_id)}
              className="inline-flex items-center gap-2 font-mono-hud text-sm text-zinc-100 hover:text-[#ff6b00]"
              data-testid="copy-order-id"
            >
              {order.order_id.slice(0, 8).toUpperCase()}
              <Copy className="h-3.5 w-3.5" />
            </button>
          </Cell>
          <Cell label="Package">
            <span className="font-hud text-zinc-100">{order.package_name}</span>
          </Cell>
          {order.prestige ? (
            <Cell label="Prestige">
              <span className="font-hud text-zinc-100">{order.prestige}</span>
            </Cell>
          ) : null}
          {order.level !== null && order.level !== undefined ? (
            <Cell label="Level">
              <span className="font-hud text-zinc-100">{order.level}</span>
            </Cell>
          ) : null}
          <Cell label="PSN ID">
            <span className="font-mono-hud text-sm text-zinc-100">{order.psn_id}</span>
          </Cell>
          <Cell label="Discord">
            <span className="font-mono-hud text-sm text-zinc-100">
              {order.discord_username}
            </span>
          </Cell>
          <Cell label="Status">
            <span className="font-mono-hud text-sm text-[#ff6b00]">
              {order.status.replace("_", " ")}
            </span>
          </Cell>
          <Cell label="Amount Due">
            <span className="font-display text-lg text-[#ff6b00]">${order.price_usd}</span>
          </Cell>
        </dl>

        <button
          onClick={onReset}
          data-testid="order-reset"
          className="mt-6 inline-flex items-center gap-2 border border-zinc-800 bg-black px-4 py-2 font-mono-hud text-xs uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-[#ff6b00] hover:text-[#ff6b00]"
        >
          ↺ Submit Another Order
        </button>
      </div>
    </div>
  );
}

function Cell({ label, children }) {
  return (
    <div className="bg-[#0a0a0a] p-4">
      <div className="font-mono-hud text-[10px] uppercase tracking-[0.3em] text-zinc-500">
        {label}
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
